import {
  ArrowLeft,
  ClipboardCheck,
  Download,
  FileUp,
  Inbox,
  ListChecks,
  Pause,
  Play
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import type {
  ExportSummary,
  ManageView,
  OwnerGroupView,
  QueueSummary,
  ReviewAnswer,
  ReviewGroupView
} from "../../shared/review";
import { api, ApiError, type Session } from "./api";
import { captionOf, citationLookupUrl, ItemEvidence, ItemSummary } from "./review-evidence";
import { Empty, fmtDate, fmtInt, humanize, Notice, PanelLoading, Segmented } from "./ui";

const groupStateLabels: Record<string, string> = {
  open: "Open",
  second_look: "In review",
  with_owner: "With owner",
  resolved: "Ready to export",
  exported: "Exported",
  withdrawn: "Withdrawn"
};

export function ReviewView({ session }: { session: Session }) {
  const [queues, setQueues] = useState<QueueSummary[]>();
  const [queueId, setQueueId] = useState<string>();
  const [error, setError] = useState("");
  const [mode, setMode] = useState<"review" | "manage">("review");
  const manager = session.permissions.includes("staff.manage");

  const load = useCallback(async () => {
    try {
      const result = await api<{ queues: QueueSummary[] }>("/api/review/queues");
      setQueues(result.queues);
      setQueueId((current) => current ?? result.queues[0]?.id);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Couldn't load the review queues. Try again shortly."
      );
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const queue = queues?.find((q) => q.id === queueId);
  if (error && !queue)
    return (
      <div className="view">
        <Notice tone="error">{error}</Notice>
      </div>
    );
  if (!queues) return <PanelLoading />;
  if (!queue)
    return (
      <div className="view">
        <Empty icon={Inbox} title="No review queues">
          There are no review queues yet.
        </Empty>
      </div>
    );

  return (
    <div className="view review">
      <header className="page-head">
        <h1>Review</h1>
        {queues.length > 1 ? (
          <Segmented
            label="Queue"
            value={queue.id}
            options={queues.map((q) => ({ value: q.id, label: q.title }))}
            onChange={(next, via) => {
              setQueueId(next);
              // Hand the keyboard back to the item: Enter on a focused queue would not record.
              // The keyboard keeps focus in the group, so the next arrow moves on.
              if (via === "pointer") (document.activeElement as HTMLElement | null)?.blur();
            }}
          />
        ) : (
          <span className="chip">{queue.title}</span>
        )}
        {manager && (
          <span
            className="authority"
            data-mode="read"
            title={`Where accepted decisions go: ${queue.short} · ${queue.ownerRepo} · ${queue.targetPath}`}
          >
            <i aria-hidden="true" />
            <span>
              {queue.short} · {queue.ownerRepo} · {queue.targetPath}
            </span>
          </span>
        )}
        <span className="head-spacer" />
        <QueueMeter queue={queue} />
        {manager && (
          <Segmented
            label="Review mode"
            value={mode}
            options={[
              { value: "review", label: "Review" },
              { value: "manage", label: "Manage" }
            ]}
            onChange={(next) => {
              setMode(next);
              void load();
            }}
          />
        )}
      </header>
      {mode === "review" || !manager ? (
        <ReviewDesk key={queue.id} session={session} queue={queue} onProgress={() => void load()} />
      ) : (
        <ManageDesk
          key={queue.id}
          session={session}
          queueId={queue.id}
          onChange={() => void load()}
        />
      )}
    </div>
  );
}

function QueueMeter({ queue }: { queue: QueueSummary }) {
  const c = queue.counts;
  const total = queue.total;
  const pct = (n: number) => `${total ? (n / total) * 100 : 0}%`;
  // With many queue tabs the text is cut short; the tooltip keeps the whole of it.
  if (!c) {
    // Reviewers see only their own progress: outcome counts would hint at others' answers.
    const text = `You have answered ${fmtInt(queue.mine.groups)} of ${fmtInt(total)} ${queue.nouns.groups}`;
    return (
      <span className="review-meter" title={text}>
        <span className="review-bar" aria-hidden="true">
          <i data-tone="ok" style={{ width: pct(queue.mine.groups) }} />
        </span>
        <span>{text}</span>
      </span>
    );
  }
  const decided = c.resolved + c.exported;
  const text = `${fmtInt(decided)} decided · ${fmtInt(c.second_look)} in review · ${fmtInt(c.with_owner)} with owner · of ${fmtInt(total)} ${queue.nouns.groups}`;
  return (
    <span className="review-meter" title={text}>
      <span className="review-bar" aria-hidden="true">
        <i data-tone="ok" style={{ width: pct(decided) }} />
        <i data-tone="neutral" style={{ width: pct(c.second_look) }} />
        <i data-tone="warn" style={{ width: pct(c.with_owner) }} />
      </span>
      <span>{text}</span>
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* Reviewing                                                                   */
/* -------------------------------------------------------------------------- */

type DecisionResult = { complete: boolean; group: ReviewGroupView };

function ReviewDesk({
  session,
  queue,
  onProgress
}: {
  session: Session;
  queue: QueueSummary;
  onProgress: () => void;
}) {
  const [group, setGroup] = useState<ReviewGroupView | null>();
  const [position, setPosition] = useState(0);
  const [choice, setChoiceKey] = useState<string>();
  const [inPart, setInPart] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [done, setDone] = useState(0);
  const [started] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());
  const skipped = useRef<string[]>([]);
  const [hint, setHint] = useState("");
  const noteRef = useRef<HTMLInputElement>(null);
  const pendingG = useRef(false);
  const paused = queue.state !== "open";

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  const answers = queue.answers;
  const partial = answers.some((a) => a.inPartOf);
  // The queue summary is reloaded after every finished group; keep the callbacks below stable
  // across reloads, or the next-group effect would fetch twice at once.
  const answersRef = useRef(answers);
  answersRef.current = answers;
  /** Show an item's saved answer (or none), with the "in part" toggle matching it. */
  const setChoice = useCallback((key: string | undefined) => {
    setChoiceKey(key);
    setInPart(Boolean(key && answersRef.current.find((a) => a.key === key)?.inPartOf));
  }, []);
  /** A base answer from the bar or a key: its "in part" form when the toggle is on and one exists. */
  const choose = (base: string) => {
    const variant = answers.find((a) => a.inPartOf === base);
    setChoiceKey(inPart && variant ? variant.key : base);
  };
  const togglePart = () => {
    const next = !inPart;
    setInPart(next);
    if (!choice) return;
    const base = answers.find((a) => a.key === choice)?.inPartOf ?? choice;
    const variant = answers.find((a) => a.inPartOf === base);
    if (variant) setChoiceKey(next ? variant.key : base);
  };

  const show = useCallback(
    (next: ReviewGroupView | null, at?: number) => {
      setGroup(next);
      if (!next) return;
      const first = next.items.findIndex((item) => !item.myAnswer);
      const target = at ?? (first === -1 ? 0 : first);
      setPosition(target);
      setChoice(next.items[target]?.myAnswer?.answer);
      setNote(next.items[target]?.myAnswer?.note ?? "");
    },
    [setChoice]
  );

  const fetchNext = useCallback(async () => {
    setBusy(true);
    setError("");
    try {
      const result = await api<{ group: ReviewGroupView | null }>(
        `/api/review/queues/${queue.id}/next`,
        session,
        { method: "POST", body: JSON.stringify({ skip: skipped.current }) }
      );
      show(result.group);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Couldn't load the next item. Try again shortly."
      );
      setGroup(null);
    } finally {
      setBusy(false);
    }
  }, [queue.id, session, show]);

  useEffect(() => {
    if (!paused) void fetchNext();
    else setGroup(null);
  }, [fetchNext, paused]);

  const item = group?.items[position];
  const nouns = queue.nouns;

  function select(next: number) {
    setHint("");
    if (!group) return;
    const target = Math.max(0, Math.min(group.items.length - 1, next));
    setPosition(target);
    setChoice(group.items[target]?.myAnswer?.answer);
    setNote(group.items[target]?.myAnswer?.note ?? "");
  }

  async function record() {
    if (!group || !item || !choice || busy) return;
    const needsNote = answers.find((a) => a.key === choice)?.needsNote;
    if (needsNote && !note.trim()) {
      setHint(needsNote);
      noteRef.current?.focus();
      return;
    }
    setHint("");
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const result = await api<DecisionResult>(
        `/api/review/groups/${group.id}/decisions`,
        session,
        {
          method: "POST",
          body: JSON.stringify({ itemId: item.id, answer: choice, note: note.trim() })
        }
      );
      if (result.complete && result.group.items.every((i) => i.myAnswer)) {
        const wasComplete = group.items.every((i) => i.myAnswer);
        if (!wasComplete) setDone((n) => n + 1);
        setMessage("Recorded.");
        onProgress();
        setBusy(false);
        await fetchNext();
        return;
      }
      const nextOpen = result.group.items.findIndex((i, index) => index > position && !i.myAnswer);
      const anyOpen = result.group.items.findIndex((i) => !i.myAnswer);
      setGroup(result.group);
      const target = nextOpen !== -1 ? nextOpen : anyOpen !== -1 ? anyOpen : position;
      setPosition(target);
      setChoice(result.group.items[target]?.myAnswer?.answer);
      setNote(result.group.items[target]?.myAnswer?.note ?? "");
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Couldn't save your answer. Try again shortly."
      );
    } finally {
      setBusy(false);
    }
  }

  async function skip() {
    if (!group || busy) return;
    skipped.current = [...skipped.current, group.id].slice(-200);
    await api(`/api/review/groups/${group.id}/release`, session, { method: "POST" }).catch(
      () => {}
    );
    await fetchNext();
  }

  function changeLast() {
    if (!group) return;
    if (position > 0) return select(position - 1);
    setHint(`Your answers on a ${nouns.group} are final once you record its last ${nouns.item}.`);
  }

  const keyHandler = useRef<(event: KeyboardEvent) => void>(() => {});
  keyHandler.current = (event: KeyboardEvent) => {
    const target = event.target as HTMLElement | null;
    const typing = target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName);
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    if (typing) {
      if (event.key === "Enter" && target === noteRef.current) {
        event.preventDefault();
        void record();
      } else if (event.key === "Escape") noteRef.current?.blur();
      return;
    }
    // The shell's "g then key" module shortcuts win over single-key answers.
    if (event.key === "g") {
      pendingG.current = true;
      window.setTimeout(() => (pendingG.current = false), 1200);
      return;
    }
    if (pendingG.current) return;
    const key = event.key.toLowerCase();
    const answer = answers.find((a) => a.shortcut && a.shortcut === key);
    if (answer && group) {
      event.preventDefault();
      choose(answer.key);
    } else if (key === "p" && partial && group) {
      event.preventDefault();
      togglePart();
    } else if (key === "n" && group) {
      event.preventDefault();
      noteRef.current?.focus();
    } else if (
      event.key === "Enter" &&
      group &&
      target?.tagName !== "BUTTON" &&
      target?.tagName !== "A"
    ) {
      event.preventDefault();
      void record();
    } else if (key === "j") select(position + 1);
    else if (key === "k") select(position - 1);
    else if (key === "o" && item?.fields.evidence_url)
      window.open(item.fields.evidence_url, "_blank", "noopener");
    else if (key === "l" && queue.layout === "added-citation" && item) {
      const lookup = citationLookupUrl(item.fields.cite ?? "");
      if (lookup) window.open(lookup, "_blank", "noopener");
    } else if (key === "z") changeLast();
    else if (key === "x") void skip();
  };
  useEffect(() => {
    const listener = (event: KeyboardEvent) => keyHandler.current(event);
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  }, []);

  const minutes = Math.max(0, Math.round((now - started) / 60_000));
  const claimUntil = group?.claimExpiresAt
    ? new Date(group.claimExpiresAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : null;
  const caption = group?.items[0] ? captionOf(queue.layout, group.items[0].fields) : "";

  return (
    <>
      <div className="toolbar review-strip" role="group" aria-label="Group">
        {group ? (
          <>
            <span>
              {queue.layout === "case-group" && (
                <>
                  Group <b className="mono">{group.groupKey}</b> ·{" "}
                </>
              )}
              {group.items.length > 1 && <>{plural(group.items.length, nouns.item)} · </>}
              {caption}
            </span>
            {group.items.length > 1 && (
              <span>
                {capital(nouns.item)} <b>{position + 1}</b> of {group.items.length}{" "}
                <kbd className="key">K</kbd> <kbd className="key">J</kbd>
              </span>
            )}
          </>
        ) : (
          <span>{busy ? `Finding the next ${nouns.group}…` : "Nothing open"}</span>
        )}
        <span>
          This session: <b>{done} done</b> · {minutes} min
        </span>
        <span className="toolbar-note">
          {claimUntil && <>Held for you until {claimUntil} · </>}
          {group && (
            <button type="button" className="link-btn" onClick={() => void skip()}>
              <kbd className="key">X</kbd> skip
            </button>
          )}
        </span>
      </div>
      {error && (
        <Notice tone="error" className="toolbar-notice">
          {error}
        </Notice>
      )}
      {hint && (
        <Notice tone="info" className="toolbar-notice">
          {hint}
        </Notice>
      )}
      <p className="sr-only" role="status" aria-live="polite">
        {message}
      </p>
      <div className="review-body">
        <main className="review-main" aria-label="Item">
          {paused ? (
            <Empty icon={Pause} title={`This queue is ${queue.state}`}>
              A manager paused it, usually to make the guideline clearer. Your answers so far are
              saved.
            </Empty>
          ) : group === undefined || (busy && !group) ? (
            <PanelLoading />
          ) : !group || !item ? (
            <Empty
              icon={ClipboardCheck}
              title={queue.total === 0 ? `No ${nouns.items} yet` : "Nothing to review right now"}
              action={
                <button type="button" className="btn btn-default" onClick={() => void fetchNext()}>
                  Check again
                </button>
              }
            >
              {queue.total === 0
                ? `This queue is ready, but empty. ${capital(nouns.items)} appear here once a manager adds the first batch.`
                : "You've answered everything open to you here, or someone else is looking at it. More arrives with the next batch."}
            </Empty>
          ) : (
            <ItemEvidence
              queue={queue}
              group={group}
              position={position}
              onSelect={select}
              canStacks={session.permissions.includes("stacks.read")}
            />
          )}
        </main>
        <Guidance queue={queue} />
      </div>
      <AnswerBar
        answers={answers}
        choice={choice}
        onChoice={choose}
        inPart={partial ? inPart : null}
        onInPart={togglePart}
        note={note}
        onNote={setNote}
        noteRef={noteRef}
        disabled={!group || busy || paused}
        onRecord={() => void record()}
      />
    </>
  );
}

function Guidance({ queue }: { queue: QueueSummary }) {
  const { nouns } = queue;
  const shown = queue.answers.filter((a) => !a.inPartOf);
  const several = queue.layout !== "overruling";
  const keys: [string, string[]][] = [
    [shown.map((a) => a.label).join(" / "), shown.map((a) => a.shortcut.toUpperCase())],
    ...(queue.answers.some((a) => a.inPartOf)
      ? ([["In part on / off", ["P"]]] as [string, string[]][])
      : []),
    ["Note", ["N"]],
    ["Record and next", ["Enter"]],
    ...(several ? ([[`Previous / next ${nouns.item}`, ["K", "J"]]] as [string, string[]][]) : []),
    ["Open on CourtListener", ["O"]],
    ...(several
      ? ([[`Back one ${nouns.item} (to change it)`, ["Z"]]] as [string, string[]][])
      : []),
    [`Skip this ${nouns.group}`, ["X"]]
  ];
  return (
    <aside className="review-side" aria-label="Guideline">
      <section className="panel-section">
        <h3 className="review-guide-title">
          How to answer <span className="muted">v{queue.guideline.version}</span>
        </h3>
        <MarkdownLite body={queue.guideline.body} />
      </section>
      <section className="panel-section">
        <h3>What happens to your answer</h3>
        <dl className="review-rules">
          {shown
            .filter((a) => !a.unsure)
            .map((a) => (
              <div key={a.key}>
                <dt>
                  <b>{a.label}:</b> {a.effect}
                </dt>
                <dd>{a.changesData ? "2 must agree" : "1 reviewer"}</dd>
              </div>
            ))}
          <div>
            <dt>
              <b>Unsure:</b> another reviewer looks at it.
            </dt>
            <dd>2 unsure: owner</dd>
          </div>
          <div>
            <dt>Reviewers disagree</dt>
            <dd>owner decides</dd>
          </div>
        </dl>
        {queue.finalApproval && <p className="review-small">{queue.finalApproval}</p>}
        <p className="muted review-small">
          Your answers on a {nouns.group} are final once you record its last {nouns.item}. Some{" "}
          {nouns.groups} get a second, independent look. Nothing here tells you whether anyone else
          has answered.
        </p>
      </section>
      <section className="panel-section">
        <h3>Notes</h3>
        <p className="review-rule-box">
          Write only about the public court records. No client names and no text from briefs. Your
          note is saved with your name.
        </p>
      </section>
      <section className="panel-section">
        <h3>Shortcuts</h3>
        <dl className="review-keys">
          {keys.map(([label, list]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>
                {list.map((key) => (
                  <kbd className="key" key={key}>
                    {key}
                  </kbd>
                ))}
              </dd>
            </div>
          ))}
        </dl>
      </section>
    </aside>
  );
}

/** Paragraphs, **bold**, *italic* and > quotes: all a guideline uses. */
function MarkdownLite({ body }: { body: string }) {
  const inline = (text: string) =>
    text
      .split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g)
      .map((part, i) =>
        part.startsWith("**") ? (
          <b key={i}>{part.slice(2, -2)}</b>
        ) : part.startsWith("*") && part.length > 2 ? (
          <em key={i}>{part.slice(1, -1)}</em>
        ) : (
          part
        )
      );
  return (
    <div className="review-guide">
      {body
        .split(/\n\s*\n/)
        .map((block) => block.trim())
        .filter(Boolean)
        .map((block, i) =>
          block.startsWith(">") ? (
            <blockquote key={i}>{inline(block.replace(/^>\s?/, ""))}</blockquote>
          ) : (
            <p key={i}>{inline(block)}</p>
          )
        )}
    </div>
  );
}

function AnswerBar({
  answers,
  choice,
  onChoice,
  inPart,
  onInPart,
  note,
  onNote,
  noteRef,
  disabled,
  onRecord
}: {
  answers: ReviewAnswer[];
  choice: string | undefined;
  onChoice: (key: string) => void;
  /** null: the queue has no "in part" answers. */
  inPart: boolean | null;
  onInPart: () => void;
  note: string;
  onNote: (note: string) => void;
  noteRef: React.RefObject<HTMLInputElement | null>;
  disabled: boolean;
  onRecord: () => void;
}) {
  const needsNote = answers.find((a) => a.key === choice)?.needsNote;
  return (
    <form
      className="review-answers"
      aria-label="Answer"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        onRecord();
      }}
    >
      <div className="review-choices" role="radiogroup" aria-label="Answer">
        {answers
          .filter((answer) => !answer.inPartOf)
          .map((answer) => (
            <button
              key={answer.key}
              type="button"
              role="radio"
              aria-checked={
                choice === answer.key ||
                answers.find((a) => a.key === choice)?.inPartOf === answer.key
              }
              className="review-choice"
              disabled={disabled}
              onClick={(event) => {
                onChoice(answer.key);
                event.currentTarget.blur();
              }}
              aria-keyshortcuts={answer.shortcut.toUpperCase()}
            >
              {answer.label}
              <kbd className="key" aria-hidden="true">
                {answer.shortcut.toUpperCase()}
              </kbd>
            </button>
          ))}
      </div>
      {inPart !== null && (
        <button
          type="button"
          className="review-choice review-part"
          aria-pressed={inPart}
          disabled={
            disabled ||
            Boolean(
              choice &&
                !answers.some((a) => a.inPartOf && (a.inPartOf === choice || a.key === choice))
            )
          }
          onClick={(event) => {
            onInPart();
            event.currentTarget.blur();
          }}
          aria-keyshortcuts="P"
          title="Only part of the earlier decision was overruled"
        >
          In part
          <kbd className="key" aria-hidden="true">
            P
          </kbd>
        </button>
      )}
      <label className="review-note">
        <span className="sr-only">{needsNote ?? "Note (optional, public court records only)"}</span>
        <input
          ref={noteRef}
          value={note}
          maxLength={500}
          onChange={(event) => onNote(event.target.value)}
          placeholder={needsNote ?? "Note (optional): public court records only"}
          aria-required={Boolean(needsNote)}
          disabled={disabled}
          aria-keyshortcuts="N"
        />
        <kbd className="key" aria-hidden="true">
          N
        </kbd>
      </label>
      <button
        type="submit"
        className="btn btn-primary"
        disabled={disabled || !choice || Boolean(needsNote && !note.trim())}
        title={needsNote && !note.trim() ? needsNote : undefined}
      >
        Record and next
        <kbd className="btn-kbd" aria-hidden="true">
          Enter
        </kbd>
      </button>
    </form>
  );
}

/* -------------------------------------------------------------------------- */
/* Managing                                                                    */
/* -------------------------------------------------------------------------- */

type BatchGroup = {
  id: string;
  groupKey: string;
  state: string;
  inSample: boolean;
  padding: boolean;
  exportedIn: string | null;
  items: number;
  caption: string;
};

function ManageDesk({
  session,
  queueId,
  onChange
}: {
  session: Session;
  queueId: string;
  onChange: () => void;
}) {
  const [view, setView] = useState<ManageView>();
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [ownerGroup, setOwnerGroup] = useState<string>();
  const [browsing, setBrowsing] = useState<string>();

  const load = useCallback(async () => {
    try {
      setView(await api<ManageView>(`/api/review/queues/${queueId}/manage`));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The queue could not be loaded");
    }
  }, [queueId]);
  useEffect(() => {
    void load();
  }, [load]);

  async function act(path: string, body: unknown, done: string) {
    setError("");
    setNotice("");
    try {
      await api(path, session, { method: "POST", body: JSON.stringify(body) });
      setNotice(done);
      await load();
      onChange();
      return true;
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "That didn't work");
      return false;
    }
  }

  if (ownerGroup)
    return (
      <OwnerGroup
        session={session}
        groupId={ownerGroup}
        onBack={() => {
          setOwnerGroup(undefined);
          void load();
          onChange();
        }}
      />
    );
  if (!view) return error ? <Notice tone="error">{error}</Notice> : <PanelLoading />;

  return (
    <div className="review-manage">
      {error && <Notice tone="error">{error}</Notice>}
      {notice && <Notice tone="success">{notice}</Notice>}
      {view.queue.state !== "open" && (
        <Notice tone="warn" quiet>
          This queue is {view.queue.state}: reviewers can't take or answer groups.
        </Notice>
      )}

      <section className="review-card" aria-labelledby="owner-queue">
        <h2 id="owner-queue">With you ({view.withOwner.length})</h2>
        <p className="muted">
          Disagreements, and items two reviewers marked unsure. Your ruling is final.
        </p>
        {view.withOwner.length === 0 ? (
          <p className="muted">Nothing is waiting for a ruling.</p>
        ) : (
          <table className="records fixed">
            <thead>
              <tr>
                <th style={{ width: 120 }}>Group</th>
                <th>Caption</th>
                <th className="right" style={{ width: 90 }}>
                  Records
                </th>
                <th style={{ width: 110 }} />
              </tr>
            </thead>
            <tbody>
              {view.withOwner.map((g) => (
                <tr key={g.id} onClick={() => setOwnerGroup(g.id)}>
                  <td className="mono">{g.groupKey}</td>
                  <td>{g.caption}</td>
                  <td className="right num">{g.items}</td>
                  <td>
                    <button type="button" className="btn btn-default">
                      Rule
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="review-card" aria-labelledby="batches">
        <h2 id="batches">Batches</h2>
        {view.batches.length === 0 && <p className="muted">No batch imported yet.</p>}
        {view.batches.map((batch) => (
          <div className="review-batch" key={batch.id}>
            <div className="review-batch-head">
              <b>{batch.fileName}</b>
              <span className="muted">
                {fmtInt(batch.rows)} records · {fmtInt(batch.groups)} groups · {batch.sampleGroups}{" "}
                in the agreement sample · imported by {batch.importedBy} {fmtDate(batch.importedAt)}{" "}
                · code version{" "}
                <span className="mono">
                  {(typeof batch.receipt.commit === "string" ? batch.receipt.commit : "").slice(
                    0,
                    7
                  )}
                </span>
              </span>
            </div>
            <div className="review-states">
              {Object.entries(batch.counts)
                .filter(([, n]) => n > 0)
                .map(([state, n]) => (
                  <span key={state} className="chip">
                    {groupStateLabels[state]} {fmtInt(n)}
                  </span>
                ))}
            </div>
            <div className="review-batch-actions">
              <button
                type="button"
                className="btn btn-primary"
                disabled={
                  batch.counts.resolved - batch.correctable.reduce((n, c) => n + c.groups, 0) <= 0
                }
                onClick={() =>
                  void act(
                    `/api/review/batches/${batch.id}/exports`,
                    {},
                    "Export created. Download it below."
                  )
                }
              >
                <ListChecks aria-hidden="true" /> Export ready groups
              </button>
              {batch.correctable.map((c) => (
                <button
                  key={c.exportId}
                  type="button"
                  className="btn btn-default"
                  onClick={() =>
                    void act(
                      `/api/review/batches/${batch.id}/exports`,
                      { corrects: c.exportId },
                      "Correction export created."
                    )
                  }
                >
                  Correct export {c.exportId.slice(0, 8)} ({c.groups} groups)
                </button>
              ))}
              <button
                type="button"
                className="btn btn-default"
                onClick={() => setBrowsing(browsing === batch.id ? undefined : batch.id)}
                aria-expanded={browsing === batch.id}
              >
                Groups
              </button>
              <ReasonAction
                label="Withdraw batch"
                danger
                onSubmit={(reason) =>
                  act(`/api/review/batches/${batch.id}/withdraw`, { reason }, "Batch withdrawn.")
                }
              />
            </div>
            {browsing === batch.id && <BatchGroups batchId={batch.id} onOpen={setOwnerGroup} />}
          </div>
        ))}
      </section>

      <section className="review-card" aria-labelledby="exports">
        <h2 id="exports">Exports</h2>
        <p className="muted">
          Download an export and give it to an agent working in our legal database. It runs{" "}
          <span className="mono">{view.queue.applyCommand}</span> and opens the pull request. Record
          what happened here.
          {view.queue.finalApproval && <> {view.queue.finalApproval}</>}
        </p>
        {view.exports.length === 0 ? (
          <p className="muted">No exports yet.</p>
        ) : (
          view.exports.map((x) => <ExportRow key={x.id} item={x} onEvent={act} />)
        )}
      </section>

      <Agreement view={view} />

      <section className="review-card" aria-labelledby="import">
        <h2 id="import">Import a batch</h2>
        <ImportForm session={session} queueId={queueId} onDone={() => (void load(), onChange())} />
      </section>

      <section className="review-card" aria-labelledby="queue-state">
        <h2 id="queue-state">Queue</h2>
        <ReasonAction
          label={view.queue.state === "open" ? "Pause queue" : "Reopen queue"}
          icon={view.queue.state === "open" ? Pause : Play}
          onSubmit={(reason) =>
            act(
              `/api/review/queues/${queueId}/state`,
              { state: view.queue.state === "open" ? "paused" : "open", reason },
              view.queue.state === "open" ? "Queue paused." : "Queue reopened."
            )
          }
        />
      </section>

      <section className="review-card" aria-labelledby="activity">
        <h2 id="activity">Activity</h2>
        <ol className="review-activity">
          {view.activity.map((entry, i) => (
            <li key={i}>
              <span className="muted">{fmtDate(entry.at)}</span> <b>{entry.actor}</b>{" "}
              {humanize(entry.action.replace(".", " "))}
              {entry.target && <span className="mono"> {entry.target.slice(0, 8)}</span>}
              {typeof entry.detail.reason === "string" && <> · {entry.detail.reason}</>}
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}

function ReasonAction({
  label,
  onSubmit,
  danger = false,
  icon: Icon
}: {
  label: string;
  onSubmit: (reason: string) => Promise<boolean>;
  danger?: boolean;
  icon?: React.ComponentType<{ "aria-hidden"?: boolean }>;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  if (!open)
    return (
      <button
        type="button"
        className={`btn ${danger ? "btn-quiet" : "btn-default"}`}
        onClick={() => setOpen(true)}
      >
        {Icon && <Icon aria-hidden={true} />} {label}
      </button>
    );
  return (
    <form
      className="review-inline"
      onSubmit={(event) => {
        event.preventDefault();
        void onSubmit(reason).then((ok) => ok && (setOpen(false), setReason("")));
      }}
    >
      <input
        aria-label={`Reason: ${label}`}
        placeholder="Reason (recorded)"
        value={reason}
        minLength={5}
        maxLength={500}
        required
        autoFocus
        onChange={(event) => setReason(event.target.value)}
      />
      <button type="submit" className={`btn ${danger ? "btn-danger" : "btn-primary"}`}>
        {label}
      </button>
      <button type="button" className="btn btn-quiet" onClick={() => setOpen(false)}>
        Cancel
      </button>
    </form>
  );
}

function BatchGroups({ batchId, onOpen }: { batchId: string; onOpen: (id: string) => void }) {
  const [groups, setGroups] = useState<BatchGroup[]>();
  useEffect(() => {
    void api<{ groups: BatchGroup[] }>(`/api/review/batches/${batchId}/groups`).then((r) =>
      setGroups(r.groups)
    );
  }, [batchId]);
  if (!groups) return <PanelLoading />;
  return (
    <table className="records fixed review-groups">
      <thead>
        <tr>
          <th style={{ width: 110 }}>Group</th>
          <th>Caption</th>
          <th className="right" style={{ width: 80 }}>
            Records
          </th>
          <th style={{ width: 150 }}>State</th>
          <th style={{ width: 120 }}>Flags</th>
        </tr>
      </thead>
      <tbody>
        {groups.map((g) => (
          <tr key={g.id} onClick={() => onOpen(g.id)}>
            <td className="mono">{g.groupKey}</td>
            <td>{g.caption}</td>
            <td className="right num">{g.items}</td>
            <td>{groupStateLabels[g.state]}</td>
            <td className="muted">
              {[g.inSample && "sample", g.padding && "padding"].filter(Boolean).join(", ")}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ExportRow({
  item,
  onEvent
}: {
  item: ExportSummary;
  onEvent: (path: string, body: unknown, done: string) => Promise<boolean>;
}) {
  const [event, setEvent] = useState("applied");
  const [detail, setDetail] = useState("");
  const terminal = ["merged", "failed", "abandoned"].includes(item.status);
  return (
    <div className="review-export">
      <div className="review-batch-head">
        <b className="mono">{item.fileName}</b>
        <span className="muted">
          {item.kind === "correction" ? `Correction of ${item.corrects?.slice(0, 8)} · ` : ""}
          {plural(item.groups, "group")} · {plural(item.items, "record")} ·{" "}
          {plural(item.changes, "change")} · by {item.createdBy} {fmtDate(item.createdAt)} · SHA-256{" "}
          <span className="mono">{item.fileSha256.slice(0, 12)}</span>
        </span>
        <span className="chip">{humanize(item.status)}</span>
      </div>
      <div className="review-batch-actions">
        <a className="btn btn-default" href={`/api/review/exports/${item.id}/download`} download>
          <Download aria-hidden="true" /> Download
        </a>
        {!terminal && (
          <form
            className="review-inline"
            onSubmit={(e) => {
              e.preventDefault();
              const body =
                event === "pr_opened"
                  ? { event, url: detail.trim() }
                  : detail.trim()
                    ? { event, message: detail.trim() }
                    : { event };
              void onEvent(
                `/api/review/exports/${item.id}/events`,
                body,
                `Recorded: ${humanize(event)}.`
              ).then((ok) => ok && setDetail(""));
            }}
          >
            <select
              aria-label="What happened"
              value={event}
              onChange={(e) => setEvent(e.target.value)}
            >
              <option value="applied">Applied in our legal database</option>
              <option value="pr_opened">PR opened</option>
              <option value="merged">Merged</option>
              <option value="failed">Failed</option>
              <option value="abandoned">Abandoned</option>
            </select>
            <input
              aria-label={event === "pr_opened" ? "PR link" : "Detail"}
              placeholder={
                event === "pr_opened"
                  ? "https://github.com/evidite/core-legal/pull/…"
                  : "Detail (optional)"
              }
              value={detail}
              required={event === "pr_opened"}
              onChange={(e) => setDetail(e.target.value)}
            />
            <button type="submit" className="btn btn-default">
              Record
            </button>
          </form>
        )}
      </div>
      {item.events.length > 0 && (
        <ol className="review-activity">
          {item.events.map((e, i) => (
            <li key={i}>
              <span className="muted">{fmtDate(e.at)}</span> {humanize(e.event)} by {e.actor}
              {typeof e.detail.url === "string" && (
                <>
                  {" · "}
                  <a href={e.detail.url} target="_blank" rel="noopener noreferrer">
                    {e.detail.url.replace(
                      /^https:\/\/github\.com\/(?:evidite|GenieCore-Inc)\//,
                      ""
                    )}
                  </a>
                </>
              )}
              {typeof e.detail.message === "string" && <> · {e.detail.message}</>}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function plural(n: number, word: string) {
  return `${fmtInt(n)} ${word}${n === 1 ? "" : "s"}`;
}

function capital(word: string) {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

function pct(value: number | null) {
  return value === null ? "–" : `${Math.round(value * 100)}%`;
}

function Agreement({ view }: { view: ManageView }) {
  const a = view.agreement;
  return (
    <section className="review-card" aria-labelledby="agreement">
      <h2 id="agreement">Agreement</h2>
      {a.sharpenGuideline && (
        <Notice tone="warn" quiet>
          Agreement is below 0.67 on {a.sampleGroupsComplete} sample groups. Sharpen the guideline;
          you may pause the queue meanwhile.
        </Notice>
      )}
      <dl className="review-figures">
        <div>
          <dt>Krippendorff's alpha</dt>
          <dd>{a.alpha === null ? "–" : a.alpha.toFixed(2)}</dd>
        </div>
        <div>
          <dt>Raw agreement</dt>
          <dd>{pct(a.rawAgreement)}</dd>
        </div>
        <div>
          <dt>Sample groups answered twice</dt>
          <dd>
            {a.sampleGroupsComplete} of {a.sampleGroups}
          </dd>
        </div>
        <div>
          <dt>Record pairs (unsure left out)</dt>
          <dd>
            {a.pairs} ({a.unsurePairs} unsure)
          </dd>
        </div>
        <div>
          <dt>Second looks confirming a change</dt>
          <dd>
            {pct(a.confirmation.rate)} ({a.confirmation.confirmed} of {a.confirmation.checked})
          </dd>
        </div>
      </dl>
      <p className="muted review-small">
        Alpha is measured only on the agreement sample, drawn at import before anyone answered. It
        is evidence for a later ruling, never a switch. Per-reviewer figures are not a ranking.
      </p>
      {a.reviewers.length > 0 && (
        <table className="records fixed">
          <thead>
            <tr>
              <th>Reviewer</th>
              <th className="right">Sample answers</th>
              <th className="right">Matching the outcome</th>
            </tr>
          </thead>
          <tbody>
            {a.reviewers.map((r) => (
              <tr key={r.name}>
                <td>{r.name}</td>
                <td className="right num">{r.sampleAnswers}</td>
                <td className="right num">{pct(r.rate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function ImportForm({
  session,
  queueId,
  onDone
}: {
  session: Session;
  queueId: string;
  onDone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [problems, setProblems] = useState<string[]>([]);
  const [result, setResult] = useState("");
  const formRef = useRef<HTMLFormElement>(null);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setProblems([]);
    setResult("");
    try {
      const response = await fetch(`/api/review/queues/${queueId}/import`, {
        method: "POST",
        headers: { Accept: "application/json", "X-Evidite-CSRF": session.csrfToken },
        body: new FormData(event.currentTarget)
      });
      const payload = (await response.json().catch(() => ({}))) as {
        error?: { message?: string; problems?: string[] };
        groups?: number;
        rows?: number;
        sampleGroups?: number;
      };
      if (!response.ok) {
        setProblems(
          payload.error?.problems ?? [
            payload.error?.message ?? `Import failed (${response.status})`
          ]
        );
        return;
      }
      setResult(
        `Imported ${fmtInt(payload.rows ?? 0)} records in ${fmtInt(payload.groups ?? 0)} groups; ${payload.sampleGroups} drawn for the agreement sample.`
      );
      formRef.current?.reset();
      onDone();
    } finally {
      setBusy(false);
    }
  }
  return (
    <form ref={formRef} className="review-import" onSubmit={(e) => void submit(e)}>
      <p className="muted">
        Upload the unmarked CSV and its <span className="mono">receipt.json</span> exactly as our
        legal database's script wrote them. The receipt must name the script, its code version and
        the CSV's SHA-256.
      </p>
      <label>
        Review CSV <input type="file" name="csv" accept=".csv,text/csv" required />
      </label>
      <label>
        Receipt <input type="file" name="receipt" accept=".json,application/json" required />
      </label>
      <button type="submit" className="btn btn-primary" disabled={busy}>
        <FileUp aria-hidden="true" /> {busy ? "Checking…" : "Import"}
      </button>
      {result && <Notice tone="success">{result}</Notice>}
      {problems.length > 0 && (
        <Notice tone="error">
          <b>Refused.</b>
          <ul>
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </Notice>
      )}
    </form>
  );
}

function OwnerGroup({
  session,
  groupId,
  onBack
}: {
  session: Session;
  groupId: string;
  onBack: () => void;
}) {
  const [group, setGroup] = useState<OwnerGroupView>();
  const [queue, setQueue] = useState<QueueSummary>();
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [error, setError] = useState("");
  useEffect(() => {
    void api<{ group: OwnerGroupView }>(`/api/review/owner/groups/${groupId}`).then((r) =>
      setGroup(r.group)
    );
  }, [groupId]);
  useEffect(() => {
    if (!group) return;
    void api<{ queues: QueueSummary[] }>("/api/review/queues").then((r) =>
      setQueue(r.queues.find((q) => q.id === group.queueId))
    );
  }, [group?.queueId]);
  const byItem = useMemo(() => {
    const map = new Map<string, OwnerGroupView["decisions"]>();
    for (const d of group?.decisions ?? []) map.set(d.itemId, [...(map.get(d.itemId) ?? []), d]);
    return map;
  }, [group]);
  if (!group || !queue) return <PanelLoading />;

  async function rule(itemId: string, answer: string) {
    setError("");
    try {
      const result = await api<{ group: OwnerGroupView }>(
        `/api/review/owner/groups/${groupId}/rulings`,
        session,
        {
          method: "POST",
          body: JSON.stringify({ itemId, answer, note: notes[itemId] ?? "" })
        }
      );
      setGroup(result.group);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "The ruling could not be recorded");
    }
  }
  const firm = queue.answers.filter((a) => !a.unsure);
  const label = (key: string) => queue.answers.find((a) => a.key === key)?.label ?? key;

  return (
    <div className="review-manage">
      <button type="button" className="btn btn-quiet review-back" onClick={onBack}>
        <ArrowLeft aria-hidden="true" /> Back to the queue
      </button>
      <section className="review-card">
        <h2>
          {queue.layout === "case-group" && (
            <>
              Group <span className="mono">{group.groupKey}</span> ·{" "}
            </>
          )}
          {captionOf(queue.layout, group.items[0]?.fields ?? {})}
        </h2>
        <p className="muted">
          {groupStateLabels[group.state]}
          {group.inSample && " · agreement sample"}
          {group.padding && " · padding look"}
          {group.exportedIn &&
            ` · exported in ${group.exportedIn.slice(0, 8)}; a ruling that changes it needs a correction export`}
        </p>
        {error && <Notice tone="error">{error}</Notice>}
        <table className="records fixed review-owner">
          <thead>
            <tr>
              <th style={{ width: 36 }}>#</th>
              <th style={{ width: 300 }}>{capital(queue.nouns.item)}</th>
              <th>Answers</th>
              <th style={{ width: 150 }}>Outcome</th>
              <th style={{ width: 330 }}>Your ruling</th>
            </tr>
          </thead>
          <tbody>
            {group.items.map((item) => (
              <tr key={item.id} className="review-owner-row">
                <td className="num">{item.position + 1}</td>
                <td title={item.question}>
                  <ItemSummary layout={queue.layout} fields={item.fields} />
                </td>
                <td>
                  {(byItem.get(item.id) ?? []).map((d) => (
                    <div key={d.id} className={d.counted ? "" : "muted"}>
                      <b>{d.reviewer}</b>
                      {d.pass === "owner" && " (ruling)"}: {label(d.answer)}
                      {d.note && <> · “{d.note}”</>}
                      {!d.counted && " (superseded or incomplete)"}
                    </div>
                  ))}
                </td>
                <td>
                  {item.outcome.status === "resolved"
                    ? `${label(item.outcome.answer)} (${item.outcome.basis})`
                    : item.outcome.status === "owner"
                      ? item.outcome.reason === "unsure"
                        ? "Unsure twice"
                        : "Disagreement"
                      : "Waiting for a look"}
                </td>
                <td>
                  <div className="review-inline">
                    {firm.map((a) => (
                      <button
                        key={a.key}
                        type="button"
                        className="btn btn-default"
                        disabled={Boolean(a.needsNote && !notes[item.id]?.trim())}
                        title={a.needsNote}
                        onClick={() => void rule(item.id, a.key)}
                      >
                        {a.label}
                      </button>
                    ))}
                    <input
                      aria-label={`Note for record ${item.position + 1}`}
                      placeholder="Note"
                      maxLength={500}
                      value={notes[item.id] ?? ""}
                      onChange={(e) => setNotes({ ...notes, [item.id]: e.target.value })}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
